/**
 * HUD.ts
 * Fixed top bar showing signal strength plus the four resources:
 * Reputation, Fanbase, Sales, Gear.
 *
 * Layout (top-right, y=0):
 *
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │  ◉ SIGNAL  ████████░░  75   ● REP  200   ● FAN  50/100  ● SALES ... │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * Signal panel (leftmost): coloured dot + "SIG" label + progress bar + value.
 * Resource panels: coloured dot + dim label + bright value.
 * Text updated every frame only when the value changes.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { ResourceSystem } from '../systems/ResourceSystem';
import type { BeaconSystem, SignalBand } from '../systems/BeaconSystem';

// ── Colours per resource ──────────────────────────────────────────────
const COLOURS = {
  reputation: 0xffb300, // amber  — crowd love
  fanbase:    0x00bcd4, // cyan   — audience size
  sales:      0x66bb6a, // green  — revenue
  gear:       0xba68c8, // purple — equipment
} as const;

const LABELS = {
  reputation: 'REP',
  fanbase:    'FAN',
  sales:      'SALES',
  gear:       'GEAR',
} as const;

/** Signal bar colour per band. */
const SIGNAL_BAND_COLOUR: Record<SignalBand, number> = {
  prophecy: 0x00ffcc, // bright cyan  — above 75
  normal:   0x66bb6a, // green        — 50–75
  reduced:  0xffaa00, // amber        — 25–50
  critical: 0xff3333, // red          — below 25
};

// ── Shared text styles ────────────────────────────────────────────────
const STYLE_LABEL = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   11,
  fill:       '#8aa39f',
});
const makeValueStyle = (colour: number) => new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   13,
  fontWeight: 'bold',
  fill:       `#${colour.toString(16).padStart(6, '0')}`,
});

const BAR_H      = 36;
const PANEL_W    = 168; // width of each resource panel
const PANEL_GAP  = 2;   // gap between resource panels
const DOT_R      = 5;   // radius of the colour indicator dot
const PAD_L      = 10;  // left padding inside panel
const VALUE_XOFF = 62;  // fixed x offset (from panel left) for the value text

// ── Signal panel constants ────────────────────────────────────────────
const SIG_PANEL_W  = 196; // wider to accommodate the progress bar
const SIG_PANEL_GAP = 8;  // gap between signal panel and first resource panel
const SIG_BAR_W    = 64;  // progress bar width in px
const SIG_BAR_H    = 5;   // progress bar height in px
const SIG_BAR_XOFF = 56;  // x offset to start of the bar (from panel left)
const SIG_VALUE_XOFF = SIG_BAR_XOFF + SIG_BAR_W + 6; // value text after bar

// ── Timer panel constants ─────────────────────────────────────────────
const TIMER_PANEL_W = 88;
const TIMER_GAP     = 6;

// ── Wave panel constants ──────────────────────────────────────────────
const WAVE_PANEL_W = 72;
const WAVE_GAP     = 4;
const WAVE_COLOUR  = 0xff6633;

export class HUD {
  private readonly container = new Container();
  private readonly bg        = new Graphics();

  // Per-resource display objects
  private readonly panels: Array<{
    dot:       Graphics;
    labelText: Text;
    valueText: Text;
    lastValue: string;
  }> = [];

  // Signal panel display objects
  private readonly sigDot       = new Graphics();
  private readonly sigLabel     = new Text('SIG', STYLE_LABEL);
  private readonly sigValueText = new Text('—', makeValueStyle(0x00ffcc));
  private readonly sigBarBg     = new Graphics();
  private readonly sigBarFill   = new Graphics();
  private lastSigValue  = '';
  private lastSignalPct = -1;
  private lastBand: SignalBand = 'normal';

  // Timer panel display objects
  private readonly timerLabel = new Text('TIME', STYLE_LABEL);
  private readonly timerValue = new Text('00:00', new TextStyle({
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize:   13,
    fill:       '#6a8a86',
  }));
  private lastElapsedSec = -1;

  // Wave panel display objects
  private readonly waveLabel = new Text('WAVE', STYLE_LABEL);
  private readonly waveValue = new Text('—', new TextStyle({
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize:   13,
    fontWeight: 'bold',
    fill:       `#${WAVE_COLOUR.toString(16).padStart(6, '0')}`,
  }));
  private lastWave = -1;

  private beaconSystem: BeaconSystem | null = null;

  private screenW     = 800;
  /** Current signal bar draw width — set by layout(), read by update(). */
  private currentSigBarW = SIG_BAR_W;
  /** True when using compact (sub-desktop) layout — affects value formatting. */
  private isCompact   = false;

  constructor(ui: Container, private readonly resources: ResourceSystem) {
    this.container.addChild(this.bg);
    ui.addChild(this.container);
    this.buildPanels();
  }

  // ── Public API ────────────────────────────────────────────────────────

  /**
   * Wire up the BeaconSystem once it's available.
   * Call once in Game.start() after BeaconSystem is created.
   */
  setBeacon(bs: BeaconSystem): void {
    this.beaconSystem = bs;
  }

  /** Call once after the canvas is sized and on every window resize. */
  resize(w: number, _h: number): void {
    this.screenW = w;
    this.layout();
  }

  /** Refresh displayed values. Call every frame (or every update). */
  update(): void {
    // ── Resource panels ─────────────────────────────────────────────────
    const s = this.resources.snapshot;
    const values: string[] = [
      String(s.reputation),
      // Compact layout hides the fanbase cap to save space
      this.isCompact ? String(s.fanbase) : `${s.fanbase} / ${s.fanbaseCap}`,
      String(s.sales),
      String(s.gear),
    ];

    for (let i = 0; i < this.panels.length; i++) {
      const p = this.panels[i];
      if (values[i] !== p.lastValue) {
        p.valueText.text = values[i];
        p.lastValue      = values[i];
      }
    }

    // ── Signal panel ────────────────────────────────────────────────────
    if (!this.beaconSystem) return;

    const sig  = this.beaconSystem.signal;
    const band = this.beaconSystem.signalBand;
    const pct  = sig / 100;

    // Redraw bar only when value changes
    if (Math.abs(pct - this.lastSignalPct) > 0.004 || band !== this.lastBand) {
      const colour = SIGNAL_BAND_COLOUR[band];

      // Re-colour the dot
      this.sigDot.clear();
      this.sigDot.beginFill(colour, 0.9);
      this.sigDot.drawCircle(0, 0, DOT_R);
      this.sigDot.endFill();

      // Redraw progress bar fill (width comes from current layout tier)
      this.sigBarFill.clear();
      this.sigBarFill.beginFill(colour, 0.85);
      this.sigBarFill.drawRect(0, 0, Math.max(0, this.currentSigBarW * pct), SIG_BAR_H);
      this.sigBarFill.endFill();

      // Update value text colour to match band
      (this.sigValueText.style as TextStyle).fill =
        `#${colour.toString(16).padStart(6, '0')}`;

      this.lastSignalPct = pct;
      this.lastBand = band;
    }

    const sigStr = Math.round(sig).toString();
    if (sigStr !== this.lastSigValue) {
      this.sigValueText.text = sigStr;
      this.lastSigValue = sigStr;
    }
  }

  /** Update the elapsed-time display. Call each frame with seconds since game start. */
  setElapsed(seconds: number): void {
    const s = Math.floor(seconds);
    if (s === this.lastElapsedSec) return;
    this.lastElapsedSec = s;
    const mm = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    this.timerValue.text = `${mm}:${ss}`;
  }

  /** Update the wave counter. Call each frame with the current wave index. */
  setWave(wave: number): void {
    if (wave === this.lastWave) return;
    this.lastWave = wave;
    this.waveValue.text = wave === 0 ? '—' : String(wave);
  }

  // ── Build ─────────────────────────────────────────────────────────────

  private buildPanels(): void {
    const keys = ['reputation', 'fanbase', 'sales', 'gear'] as const;

    for (let i = 0; i < keys.length; i++) {
      const key    = keys[i];
      const colour = COLOURS[key];
      const label  = LABELS[key];

      const dot = new Graphics();
      dot.beginFill(colour, 0.9);
      dot.drawCircle(0, 0, DOT_R);
      dot.endFill();

      const labelText = new Text(label, STYLE_LABEL);
      const valueText = new Text('—', makeValueStyle(colour));

      this.container.addChild(dot, labelText, valueText);
      this.panels.push({ dot, labelText, valueText, lastValue: '' });
    }

    // Signal panel elements
    this.sigDot.beginFill(0x00ffcc, 0.9);
    this.sigDot.drawCircle(0, 0, DOT_R);
    this.sigDot.endFill();

    // Progress bar background track
    this.sigBarBg.beginFill(0x1a3a34, 0.7);
    this.sigBarBg.drawRect(0, 0, SIG_BAR_W, SIG_BAR_H);
    this.sigBarBg.endFill();

    this.container.addChild(
      this.timerLabel, this.timerValue,
      this.waveLabel, this.waveValue,
      this.sigDot, this.sigLabel,
      this.sigBarBg, this.sigBarFill,
      this.sigValueText,
    );
  }

  private layout(): void {
    const W = this.screenW;

    // ── Responsive tier ─────────────────────────────────────────────────────
    // Full desktop layout requires ~924 px. Below that we use a fluid compact
    // layout that always fits within W − 24 px of horizontal space.
    //
    // Formula: avail = panelW*5.16 + 23
    //   where sigW = panelW * 1.16, sigGap = 8, panelGap = 5, 4 panels
    const FULL_W = TIMER_PANEL_W + TIMER_GAP + WAVE_PANEL_W + WAVE_GAP + SIG_PANEL_W + SIG_PANEL_GAP + PANEL_W * 4 + PANEL_GAP * 3 + 24;

    let sigW: number, sigGap: number, sigBarW: number, sigBarXoff: number;
    let panelW: number, panelGap: number, valueXoff: number, padL: number;
    let barH: number, showSigBar: boolean, showTimerLabel: boolean;
    let lblSize: number, valSize: number;
    let timerW: number, timerGap: number;
    let waveW: number, waveGap: number;

    if (W >= FULL_W) {
      // ── Desktop: use original fixed constants ──────────────────────────
      sigW       = SIG_PANEL_W;  sigGap    = SIG_PANEL_GAP;
      sigBarW    = SIG_BAR_W;    sigBarXoff = SIG_BAR_XOFF;
      panelW     = PANEL_W;      panelGap  = PANEL_GAP;
      valueXoff  = VALUE_XOFF;   padL      = PAD_L;
      barH       = BAR_H;        showSigBar = true;
      lblSize    = 11;           valSize   = 13;
      timerW     = TIMER_PANEL_W; timerGap = TIMER_GAP;
      waveW      = WAVE_PANEL_W; waveGap   = WAVE_GAP;
      showTimerLabel = true;
    } else {
      // ── Compact: fluid layout that scales with screen width ────────────
      const avail = W - 16;
      panelW     = Math.max(52, Math.floor((avail - 17) / 6.42));
      sigW       = Math.floor(panelW * 1.16);
      timerW     = Math.floor(panelW * 0.70);
      waveW      = Math.floor(panelW * 0.55);
      panelGap   = 2;
      sigGap     = 5;
      timerGap   = 4;
      waveGap    = 3;
      padL       = 5;
      barH       = panelW >= 72 ? 30 : 26;
      showSigBar = panelW >= 72;
      sigBarW    = showSigBar ? Math.floor(sigW * 0.30) : 0;
      sigBarXoff = 38;
      valueXoff  = Math.max(44, Math.floor(panelW * 0.44));
      lblSize    = panelW >= 90 ? 10 : panelW >= 65 ? 9 : 8;
      valSize    = panelW >= 90 ? 12 : panelW >= 65 ? 10 : 9;
      showTimerLabel = false;
    }

    const sigValXoff = showSigBar
      ? sigBarXoff + sigBarW + 4
      : Math.floor(sigW * 0.55);

    this.currentSigBarW = sigBarW;
    this.isCompact      = W < FULL_W;

    // ── Vertical row offset ───────────────────────────────────────────────
    // On compact screens the debug title bar occupies the top ~28 px.
    // Push the HUD strip down by that amount so the two bars stack instead
    // of overlapping. On desktop the debug title sits within the HUD bar
    // (left side, right side unused) so no offset is needed.
    const yOff = this.isCompact ? 28 : 0;

    // ── Update text styles ────────────────────────────────────────────────
    // STYLE_LABEL is shared by sigLabel + all panel labelTexts — one write updates all.
    STYLE_LABEL.fontSize = lblSize;
    for (const p of this.panels) {
      (p.valueText.style as TextStyle).fontSize = valSize;
    }
    (this.sigValueText.style as TextStyle).fontSize = valSize;
    (this.waveValue.style as TextStyle).fontSize = valSize;

    // ── Compute total width and origin ────────────────────────────────────
    const totalResourceW = this.panels.length * panelW + (this.panels.length - 1) * panelGap;
    const totalW         = timerW + timerGap + waveW + waveGap + sigW + sigGap + totalResourceW;
    const startX         = Math.max(4, W - totalW - 4);
    const midY           = yOff + barH / 2;

    // ── Background strip — anchored flush to right screen edge ───────────
    const bgLeft = startX - 4;
    this.bg.clear();
    this.bg.beginFill(0x080b10, 0.82);
    this.bg.drawRect(bgLeft, yOff, W - bgLeft, barH);
    this.bg.endFill();
    this.bg.lineStyle(1, 0x1a3a34, 0.6);
    this.bg.moveTo(bgLeft, yOff + barH - 1);
    this.bg.lineTo(W, yOff + barH - 1);
    this.bg.lineStyle(0);

    // ── Timer panel ───────────────────────────────────────────────────────
    const tx = startX;
    (this.timerLabel.style as TextStyle).fontSize = lblSize;
    (this.timerValue.style as TextStyle).fontSize  = valSize;
    this.timerLabel.visible = showTimerLabel;
    if (showTimerLabel) {
      this.timerLabel.position.set(tx + padL, midY - this.timerLabel.height / 2);
      this.timerValue.position.set(tx + padL + 34, midY - this.timerValue.height / 2);
    } else {
      this.timerValue.position.set(tx + padL, midY - this.timerValue.height / 2);
    }

    // Divider between timer and wave panels
    this.bg.lineStyle(1, 0x1a3a34, 0.5);
    const timerDivX = startX + timerW + timerGap / 2;
    this.bg.moveTo(timerDivX, yOff + 6);
    this.bg.lineTo(timerDivX, yOff + barH - 6);
    this.bg.lineStyle(0);

    // ── Wave panel ────────────────────────────────────────────────────────
    const wx = startX + timerW + timerGap;
    (this.waveLabel.style as TextStyle).fontSize = lblSize;
    this.waveLabel.position.set(wx + padL, midY - this.waveLabel.height / 2);
    this.waveValue.position.set(wx + padL + 36, midY - this.waveValue.height / 2);

    // Divider between wave and signal panels
    this.bg.lineStyle(1, 0x1a3a34, 0.5);
    const waveDivX = wx + waveW + waveGap / 2;
    this.bg.moveTo(waveDivX, yOff + 6);
    this.bg.lineTo(waveDivX, yOff + barH - 6);
    this.bg.lineStyle(0);

    // ── Signal panel ──────────────────────────────────────────────────────
    const sx = wx + waveW + waveGap;
    this.sigDot.position.set(sx + padL, midY);
    this.sigLabel.position.set(
      sx + padL + DOT_R + 5,
      midY - this.sigLabel.height / 2,
    );

    // Show/hide the signal progress bar on very narrow screens
    this.sigBarBg.visible   = showSigBar;
    this.sigBarFill.visible = showSigBar;
    if (showSigBar) {
      this.sigBarBg.clear();
      this.sigBarBg.beginFill(0x1a3a34, 0.7);
      this.sigBarBg.drawRect(0, 0, sigBarW, SIG_BAR_H);
      this.sigBarBg.endFill();
      this.sigBarBg.position.set(sx + sigBarXoff, midY - SIG_BAR_H / 2);
      this.sigBarFill.position.set(sx + sigBarXoff, midY - SIG_BAR_H / 2);
      this.lastSignalPct = -1; // force fill redraw on next update()
    }

    this.sigValueText.position.set(
      sx + sigValXoff,
      midY - this.sigValueText.height / 2,
    );

    // Divider between signal and resource panels
    this.bg.lineStyle(1, 0x1a3a34, 0.5);
    const divX = sx + sigW + sigGap / 2;
    this.bg.moveTo(divX, yOff + 6);
    this.bg.lineTo(divX, yOff + barH - 6);
    this.bg.lineStyle(0);

    // ── Resource panels ───────────────────────────────────────────────────
    const resStartX = sx + sigW + sigGap;
    for (let i = 0; i < this.panels.length; i++) {
      const { dot, labelText, valueText } = this.panels[i];
      const x = resStartX + i * (panelW + panelGap);
      dot.position.set(x + padL, midY);
      labelText.position.set(x + padL + DOT_R + 5, midY - labelText.height / 2);
      valueText.position.set(x + valueXoff, midY - valueText.height / 2);
    }
  }
}
